/**
 * Client-side HTML allowlist for invoice/estimate notes & terms (matches server/htmlSanitize intent).
 * Used before dangerouslySetInnerHTML on untrusted API content.
 */

function escapeHtmlText(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Plain text → paragraphs (aligned with server/utils/htmlSanitize.js). */
function plainTextToParagraphHtml(text) {
  const lines = text.split(/\r?\n/);
  const paragraphs = [];
  let currentParagraph = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed) {
      currentParagraph.push(trimmed);
    } else {
      if (currentParagraph.length > 0) {
        paragraphs.push(`<p>${escapeHtmlText(currentParagraph.join(' '))}</p>`);
        currentParagraph = [];
      }
      paragraphs.push('<p><br></p>');
    }
  }
  if (currentParagraph.length > 0) {
    paragraphs.push(`<p>${escapeHtmlText(currentParagraph.join(' '))}</p>`);
  }
  return paragraphs.join('');
}

const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'STRONG',
  'EM',
  'B',
  'I',
  'U',
  'UL',
  'OL',
  'LI',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'A',
  'SPAN',
  'DIV',
]);

function isSafeHref(href) {
  if (!href || typeof href !== 'string') return false;
  const h = href.trim();
  return (
    /^https?:\/\//i.test(h) ||
    /^mailto:/i.test(h) ||
    /^tel:/i.test(h) ||
    h.startsWith('#') ||
    h.startsWith('/')
  );
}

function appendSanitized(parent, node) {
  if (node.nodeType === Node.TEXT_NODE) {
    parent.appendChild(node.cloneNode(true));
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const tag = node.tagName;
  if (!ALLOWED_TAGS.has(tag)) {
    node.childNodes.forEach((c) => appendSanitized(parent, c));
    return;
  }

  const el = document.createElement(tag.toLowerCase());
  if (tag === 'A') {
    const href = node.getAttribute('href') || '';
    if (isSafeHref(href)) el.setAttribute('href', href);
    if (node.getAttribute('target') === '_blank') {
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
    }
  }

  node.childNodes.forEach((c) => appendSanitized(el, c));
  parent.appendChild(el);
}

export function sanitizeRichTextHtml(html) {
  if (!html || typeof html !== 'string' || !html.trim()) return '';

  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(html);
  if (!hasHtmlTags) {
    return plainTextToParagraphHtml(html);
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const out = document.createElement('div');
  [...doc.body.childNodes].forEach((child) => appendSanitized(out, child));
  return out.innerHTML;
}
